import {
    effectiveTrailStrokeWidths,
    meanStrokeWidth,
    pointsToPathDWithStyle,
    polylineStrokeOutlineToPathD,
    trailUsesVaryingWidth,
} from "../../utils/pathAgents";

/** Renders a stroke trail with optional per-vertex widths (Path agents, Flow agents). */
export function TrailPaths({
    points,
    strokeWidths,
    smoothPaths,
    strokeColor,
    fallbackWidth,
    mergeVaryingWidthToSinglePath,
    filledVariableWidthStroke,
    strokeDashArray,
    opacity,
    trailTaperStartLength = 0,
    trailTaperEndLength = 0,
}: {
    points: { x: number; y: number }[];
    strokeWidths: number[] | undefined;
    smoothPaths: boolean;
    strokeColor: string;
    fallbackWidth: number;
    mergeVaryingWidthToSinglePath: boolean;
    filledVariableWidthStroke: boolean;
    strokeDashArray: string | undefined;
    opacity?: number;
    /** Fade stroke width over this arc length at trail start (SVG user units). */
    trailTaperStartLength?: number;
    trailTaperEndLength?: number;
}) {
    const n = points.length;
    if (n < 2) return null;

    const eff = effectiveTrailStrokeWidths(
        points,
        strokeWidths,
        fallbackWidth,
        trailTaperStartLength,
        trailTaperEndLength,
    );
    const sw = eff.length === n ? (eff as number[]) : undefined;

    if (filledVariableWidthStroke) {
        const halfs: number[] = sw
            ? sw.map((w) => Math.max(1e-6, w) / 2)
            : Array.from({ length: n }, () =>
                  Math.max(1e-6, fallbackWidth) / 2,
              );
        const d = polylineStrokeOutlineToPathD(points, halfs);
        if (!d) return null;
        return (
            <path
                d={d}
                fill={strokeColor}
                fillRule="nonzero"
                stroke="none"
                opacity={opacity}
            />
        );
    }
    const varying = trailUsesVaryingWidth(sw, n);
    const taperActive =
        trailTaperStartLength > 0 || trailTaperEndLength > 0;
    const useManyLineSegments =
        varying && (!mergeVaryingWidthToSinglePath || taperActive);

    if (!useManyLineSegments) {
        let w: number;
        if (varying && mergeVaryingWidthToSinglePath && sw) {
            w = meanStrokeWidth(sw);
        } else if (sw && sw.length === n) {
            w = sw[0];
        } else {
            w = fallbackWidth;
        }
        const d = pointsToPathDWithStyle(points, smoothPaths);
        if (!d) return null;
        return (
            <path
                d={d}
                fill="none"
                stroke={strokeColor}
                strokeWidth={w}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray={strokeDashArray}
                opacity={opacity}
            />
        );
    }

    return (
        <g opacity={opacity}>
            {Array.from({ length: n - 1 }, (_, i) => {
                const w = (sw![i] + sw![i + 1]) / 2;
                const p1 = points[i];
                const p2 = points[i + 1];
                return (
                    <line
                        key={i}
                        x1={p1.x}
                        y1={p1.y}
                        x2={p2.x}
                        y2={p2.y}
                        stroke={strokeColor}
                        strokeWidth={w}
                        strokeLinecap="round"
                        strokeDasharray={strokeDashArray}
                    />
                );
            })}
        </g>
    );
}
